/**
 * 業務日付（`YYYY-MM-DD` 文字列）の暦計算。環境非依存の純関数のみ。
 *
 * **`Date` オブジェクトを一切使わない。** タイムゾーンや DST の影響を受ける余地を
 * 実装から消すため、整数演算だけで暦日を扱う（CLAUDE.md 原則1 / SPEC §2.3）。
 * 「今日が何日か」を得るのはフロント側の責務（src/lib/date.ts の today()）で、
 * ここは受け取った文字列を計算するだけ。
 *
 * 内部表現は 1970-01-01 を 0 とする通日（epoch day）。
 * 変換は Howard Hinnant の civil ↔ days アルゴリズムによる。
 */

export type BusinessDate = string; // 'YYYY-MM-DD'
export type MonthKey = string; // 'YYYY-MM'

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;

export const isBusinessDate = (v: string): boolean => DATE_RE.test(v) && isRealDate(v);
export const isMonthKey = (v: string): boolean => MONTH_RE.test(v) && Number(v.slice(5, 7)) >= 1 && Number(v.slice(5, 7)) <= 12;

export const isLeapYear = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}

function isRealDate(d: string): boolean {
  const [y, m, day] = parts(d);
  return m >= 1 && m <= 12 && day >= 1 && day <= daysInMonth(y, m);
}

const parts = (d: string): [number, number, number] => [
  Number(d.slice(0, 4)),
  Number(d.slice(5, 7)),
  Number(d.slice(8, 10)),
];

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));
const pad4 = (n: number): string => String(n).padStart(4, '0');

/* ------------------------------------------------------------------ */
/* 通日への変換                                                          */
/* ------------------------------------------------------------------ */

/** `YYYY-MM-DD` → 1970-01-01 を 0 とする通日 */
export function toEpochDay(d: BusinessDate): number {
  const [y0, m, day] = parts(d);
  const y = y0 - (m <= 2 ? 1 : 0);
  const era = Math.floor(y / 400);
  const yoe = y - era * 400; // [0, 399]
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + day - 1; // [0, 365]
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy; // [0, 146096]
  return era * 146097 + doe - 719468;
}

/** 通日 → `YYYY-MM-DD` */
export function fromEpochDay(z0: number): BusinessDate {
  const z = z0 + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097; // [0, 146096]
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100)); // [0, 365]
  const mp = Math.floor((5 * doy + 2) / 153); // [0, 11]
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1; // [1, 31]
  const m = mp + (mp < 10 ? 3 : -9); // [1, 12]
  return `${pad4(y + (m <= 2 ? 1 : 0))}-${pad2(m)}-${pad2(day)}`;
}

/* ------------------------------------------------------------------ */
/* 日付演算                                                             */
/* ------------------------------------------------------------------ */

export const addDays = (d: BusinessDate, n: number): BusinessDate => fromEpochDay(toEpochDay(d) + n);

/** b − a（日数）。同日なら 0。 */
export const diffDays = (a: BusinessDate, b: BusinessDate): number => toEpochDay(b) - toEpochDay(a);

/** 両端を含む日数。同日なら 1（SPEC §6.1 の「同日使い切り=1日」）。 */
export const daysInclusive = (from: BusinessDate, to: BusinessDate): number => diffDays(from, to) + 1;

/** `YYYY-MM-DD` は辞書順比較がそのまま日付順比較になる */
export const minDate = (a: BusinessDate, b: BusinessDate): BusinessDate => (a <= b ? a : b);
export const maxDate = (a: BusinessDate, b: BusinessDate): BusinessDate => (a >= b ? a : b);

/* ------------------------------------------------------------------ */
/* 月                                                                   */
/* ------------------------------------------------------------------ */

export const monthOf = (d: BusinessDate): MonthKey => d.slice(0, 7);

export const monthStart = (ym: MonthKey): BusinessDate => `${ym}-01`;

export function monthEnd(ym: MonthKey): BusinessDate {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  return `${ym}-${pad2(daysInMonth(y, m))}`;
}

export function addMonths(ym: MonthKey, n: number): MonthKey {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  const total = y * 12 + (m - 1) + n;
  return `${pad4(Math.floor(total / 12))}-${pad2((total % 12) + 1)}`;
}

/** from 〜 to（両端含む）の月キーを昇順で列挙 */
export function monthRange(from: MonthKey, to: MonthKey): MonthKey[] {
  const out: MonthKey[] = [];
  for (let m = from; m <= to; m = addMonths(m, 1)) out.push(m);
  return out;
}

/** 直近 count ヶ月（endMonth を含む）を昇順で返す */
export const lastMonths = (endMonth: MonthKey, count: number): MonthKey[] =>
  monthRange(addMonths(endMonth, -(count - 1)), endMonth);
